import { Button, Icon, Section, useToast } from '@efira/ui';

import { useOverlayToken } from '../../api/queries';

/**
 * ССЫЛКА ОВЕРЛЕЯ СТРОКОЙ - ДО ПЕРВОГО ПОДКЛЮЧЕНИЯ.
 *
 * Находка аудита единицы 16 (04): главный шаг первого пути - «вставить
 * ссылку в OBS» - был спрятан за БЕЗЫМЯННОЙ иконкой в центральной трети
 * шапки, рядом с такой же безымянной иконкой кадра. Человек, который
 * только что зарегистрировался, не может выполнить единственный шаг,
 * ради которого пришёл, потому что шаг не назван.
 *
 * Решение владельца 2026-09-26 (4): до первого подключения оверлея -
 * видимая строка с кнопкой копирования в левой колонке под
 * «Подключением». После первого подключения строка сворачивается в
 * кнопку шапки, которая была там всегда.
 *
 * «ПЕРВОЕ ПОДКЛЮЧЕНИЕ» - ФАКТ СЕРВЕРА (`firstSeenAt` в `/api/overlay-token`),
 * а не флажок в браузере: человек ставит ссылку в OBS один раз и открывает
 * консоль с другой машины. Предпросмотр консоли вехой не считается - иначе
 * шаг объявлялся бы пройденным в момент первого открытия консоли.
 *
 * ИМЯ РАЗДЕЛА - «Оверлей в OBS», а не «Ссылка оверлея»: второе уже занято
 * кнопкой в шапке, и два органа с одним доступным именем - это один объект
 * в двух местах для чтеца и неоднозначный поиск для сценария (поймано
 * сразу же, `console-topbar.spec.ts`).
 *
 * ССЫЛКА ЗДЕСЬ НЕ МАСКИРУЕТСЯ, в отличие от поповера шапки. Маскировка
 * защищает от захвата экрана во время эфира; до первого подключения эфира
 * нет по определению, а замаскированная ссылка в подсказке «сделайте вот
 * это» - подсказка, которой нельзя воспользоваться.
 */
export function OverlayLinkRow() {
  const token = useOverlayToken();
  const toast = useToast();

  const url = token.data?.url ?? '';
  const connected = token.data?.firstSeenAt !== null && token.data?.firstSeenAt !== undefined;

  /* Пока ответа нет - строки нет: мелькнувшая и исчезнувшая подсказка хуже
     её отсутствия. */
  if (token.data === undefined || connected) return null;

  async function copy() {
    if (url === '') return;

    try {
      await navigator.clipboard.writeText(url);
      toast.show({ tone: 'success', title: 'Ссылка скопирована', text: 'Вставьте её в OBS' });
    } catch {
      toast.show({
        tone: 'warning',
        title: 'Браузер не дал доступ к буферу',
        text: 'Скопируйте ссылку из поля вручную.',
      });
    }
  }

  return (
    <Section title="Оверлей в OBS" id="overlay-link">
      <div className="flex flex-col gap-2 px-3 pb-3" data-left="overlay-link">
        <p className="text-foreground/80 text-sm">
          Добавьте её в OBS источником «Браузер» — и оверлей появится в эфире. Ссылка постоянная:
          вставить нужно один раз.
        </p>

        <div className="flex items-center gap-2">
          {/*
            Поле, а не абзац: ссылку выделяют и копируют руками, когда
            буфер недоступен. `readOnly` вместо `disabled` - отключённое
            поле не отдаёт выделение.
          */}
          <output
            data-left="overlay-link-url"
            aria-label="Постоянная ссылка оверлея"
            className="border-input bg-background text-foreground block min-w-0 flex-1 truncate rounded-md border px-3 py-2 font-mono text-xs"
          >
            {url}
          </output>

          <Button
            variant="secondary"
            size="s"
            data-left="overlay-link-copy"
            onClick={() => {
              void copy();
            }}
          >
            <Icon name="copy" size="sm" />
            Копировать
          </Button>
        </div>

        <p className="text-foreground/80 text-sm">
          Подсказка исчезнет сама, как только оверлей подключится.
        </p>
      </div>
    </Section>
  );
}
